export const SELECTION_CARD_ACCENT = {
    Blue: "blue",
    Amber: "amber",
    Red: "red",
} as const;

export type SelectionCardAccent = (typeof SELECTION_CARD_ACCENT)[keyof typeof SELECTION_CARD_ACCENT];

const ACCENT_STYLES: Record<SelectionCardAccent, { border: string; iconBg: string; label: string; radio: string }> = {
    blue: {
        border: "border-l-4 border-blue-400 bg-blue-50",
        iconBg: "bg-blue-100 text-blue-600",
        label: "text-blue-700",
        radio: "border-blue-500 bg-blue-500",
    },
    amber: {
        border: "border-l-4 border-amber-400 bg-amber-50",
        iconBg: "bg-amber-100 text-amber-600",
        label: "text-amber-700",
        radio: "border-amber-500 bg-amber-500",
    },
    red: {
        border: "border-l-4 border-red-400 bg-red-50",
        iconBg: "bg-red-100 text-red-600",
        label: "text-red-700",
        radio: "border-red-500 bg-red-500",
    },
};

export interface SelectionCardProps {
    /** Leading icon; rendered at a fixed 16px size. */
    icon: () => React.ReactElement;
    label: string;
    description: string;
    /** Whether this card is the active choice in its selection group. */
    selected: boolean;
    isDisabled?: boolean;
    accent?: SelectionCardAccent;
    /** Shows a trailing radio-style dot indicator in addition to the accent styling. */
    showRadioIndicator?: boolean;
    onSelect?: () => void;
}

/**
 * Generic card button for single-select option groups (e.g. escalation levels, response types).
 * Exposes `aria-pressed` so assistive tech can announce the active choice.
 */
export function SelectionCard({
    icon: Icon,
    label,
    description,
    selected,
    isDisabled = false,
    accent = SELECTION_CARD_ACCENT.Amber,
    showRadioIndicator = false,
    onSelect,
}: SelectionCardProps) {
    const colors = ACCENT_STYLES[accent];

    return (
        <button
            type="button"
            disabled={isDisabled}
            aria-pressed={selected}
            onClick={onSelect}
            className={[
                "flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors",
                selected
                    ? colors.border
                    : isDisabled
                      ? "cursor-not-allowed border-gray-100 bg-gray-50 opacity-60"
                      : "border-gray-200 bg-white hover:bg-gray-50",
            ].join(" ")}
        >
            <span
                className={["flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", selected ? colors.iconBg : "bg-gray-100 text-gray-500"].join(" ")}
            >
                <Icon />
            </span>
            <div className="flex-1">
                <p className={["text-sm font-medium", selected ? colors.label : isDisabled ? "text-gray-400" : "text-gray-800"].join(" ")}>{label}</p>
                <p className={["text-xs", isDisabled ? "text-gray-400" : "text-gray-800"].join(" ")}>{description}</p>
            </div>
            {showRadioIndicator && (
                <span
                    className={[
                        "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2",
                        selected ? colors.radio : "border-gray-300 bg-white",
                    ].join(" ")}
                >
                    {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                </span>
            )}
        </button>
    );
}
